import { useEffect, useState, type ReactNode } from 'react';
import { classic42 } from '@risk/maps';
import {
  attackTargets,
  computeBattleOdds,
  computeReinforcements,
  fortifyTargets,
  handSize,
  ownerMap,
  setValue,
  classifySet,
  type Card,
  type CardSymbol,
  type TerritoryId,
} from '@risk/engine';
import type { ClientState, GameClient } from '../net/client.js';
import { BottomBar, PhaseBar, SeatDot, Stepper, seatOf } from '../components/common.js';
import { TerritoryList } from '../components/TerritoryList.js';
import { BoardView } from '../components/BoardView.js';
import { PendingCorrections, RaiseCorrection } from '../components/Corrections.js';

const nameOf = (id: TerritoryId): string =>
  classic42.territories.find((t) => t.id === id)?.name ?? id;

export function Game({ state, client }: { state: ClientState; client: GameClient }): ReactNode {
  const { game, seatId } = state;
  const turn = game.turn;
  const active = turn?.seat ?? null;
  const mine = active !== null && active === seatId;
  const activeSeat = seatOf(game, active);

  if (game.status === 'gameOver') {
    const winner = seatOf(game, game.winner);
    return (
      <div className="card">
        <h1>{winner ? `${winner.name} takes the world` : 'Game over'}</h1>
        <p className="muted">
          {game.seats.length} players, {Object.keys(game.territories).length} territories,
          {' '}{game.setsTraded} card sets traded.
        </p>
      </div>
    );
  }

  return (
    <div className="stack">
      <PhaseBar game={game} />

      <div className="card" style={{ marginBottom: 0 }}>
        <div className="row">
          {activeSeat && <SeatDot color={activeSeat.color} />}
          <strong className="grow">
            {mine ? 'Your turn' : `${activeSeat?.name ?? 'Someone'}'s turn`}
          </strong>
          <span className="small muted">turn {turn?.number ?? 0}</span>
        </div>
      </div>

      {game.anomalies.length > 0 && (
        <div className="notice">
          <span className="grow small">
            {game.anomalies.length} thing{game.anomalies.length === 1 ? '' : 's'} in the
            record do not add up. Check them in the audit.
          </span>
        </div>
      )}

      <PendingCorrections state={state} client={client} />
      <Panel state={state} client={client} mine={mine} />
      <RaiseCorrection state={state} client={client} />
    </div>
  );
}

function Panel({
  state,
  client,
  mine,
}: {
  state: ClientState;
  client: GameClient;
  mine: boolean;
}): ReactNode {
  const { game } = state;
  switch (game.phase) {
    case 'reinforceTrade':
      return <TradePanel state={state} client={client} mine={mine} />;
    case 'reinforcePlace':
      return <PlacePanel state={state} client={client} mine={mine} />;
    case 'attack':
      return <AttackPanel state={state} client={client} mine={mine} />;
    case 'occupy':
      return <OccupyPanel state={state} client={client} mine={mine} />;
    case 'fortify':
      return <FortifyPanel state={state} client={client} mine={mine} />;
    case 'cardDraw':
      return <CardPanel state={state} client={client} mine={mine} />;
    case 'audit':
      return <AuditPanel state={state} client={client} mine={mine} />;
    default:
      return <TerritoryList game={game} seatId={state.seatId} />;
  }
}

function Advance({ label, client, disabled }: { label: string; client: GameClient; disabled?: boolean | undefined }): ReactNode {
  return (
    <BottomBar>
      <span className="grow" />
      <button className="primary" disabled={disabled} onClick={() => client.act({ type: 'advancePhase' })}>
        {label}
      </button>
    </BottomBar>
  );
}

// --- cards ---------------------------------------------------------------

const SYMBOLS: readonly CardSymbol[] = ['infantry', 'cavalry', 'artillery', 'wild'];
const SYMBOL_GLYPH: Record<CardSymbol, string> = {
  infantry: '🥾',
  cavalry: '🐎',
  artillery: '💥',
  wild: '★',
};

function TradePanel({ state, client, mine }: { state: ClientState; client: GameClient; mine: boolean }): ReactNode {
  const { game, seatId } = state;
  const hand = seatId ? game.hands[seatId] : undefined;
  const rules = game.rules;
  const [chosen, setChosen] = useState<number[]>([]);
  const [naming, setNaming] = useState<number | null>(null);

  if (!hand || !rules) return <div className="card muted">Waiting…</div>;

  const held = handSize(hand);
  const slots = hand.visible ? hand.slots : [];
  const mustTrade = held >= rules.cards.forceTradeAt;

  const selectedCards: Card[] = chosen
    .map((i) => slots[i])
    .flatMap((slot) => (slot && slot.kind === 'known' ? [slot.card] : []));

  const complete = chosen.length === 3 && selectedCards.length === 3;
  const setType = complete ? classifySet(selectedCards, rules) : null;
  const worth = setType ? setValue(setType, game.setsTraded, rules) : 0;

  const toggle = (i: number): void => {
    setChosen((current) =>
      current.includes(i) ? current.filter((x) => x !== i) : current.length >= 3 ? current : [...current, i],
    );
  };

  return (
    <>
      <div className="card">
        <h2>Your cards <span className="muted small">({held})</span></h2>
        {mustTrade && (
          <div className="notice">
            <span className="small">
              You hold {held} cards. You must trade a set before doing anything else.
            </span>
          </div>
        )}
        {slots.length === 0 && <p className="muted small">No cards yet.</p>}

        <div className="stack">
          {slots.map((slot, i) => (
            <div className="row" key={i}>
              <button
                className={`grow ${chosen.includes(i) ? 'primary' : ''}`}
                style={{ justifyContent: 'flex-start', textAlign: 'left' }}
                onClick={() => toggle(i)}
                disabled={!mine}
              >
                {slot.kind === 'known'
                  ? `${SYMBOL_GLYPH[slot.card.symbol]}  ${slot.card.territory ? nameOf(slot.card.territory) : 'Wild'}`
                  : '❓  Not named yet'}
              </button>
              <button className="small" onClick={() => setNaming(naming === i ? null : i)}>
                {slot.kind === 'known' ? 'Edit' : 'Name'}
              </button>
            </div>
          ))}
        </div>

        {naming !== null && (
          <NameCard
            onCancel={() => setNaming(null)}
            onSave={(card) => {
              client.act({ type: 'identifyCard', slot: naming, card });
              setNaming(null);
            }}
          />
        )}

        {chosen.length > 0 && (
          <div className="card" style={{ marginTop: 12, marginBottom: 0, background: 'var(--panel-2)' }}>
            {complete && setType && (
              <div className="row between">
                <span>{setType} set</span>
                <strong>{worth} armies</strong>
              </div>
            )}
            {complete && !setType && <span className="small">Those three are not a set.</span>}
            {!complete && chosen.length === 3 && (
              <span className="small muted">Name every card in the set before trading it.</span>
            )}
            {chosen.length < 3 && <span className="small muted">Pick {3 - chosen.length} more.</span>}
            <div className="row" style={{ marginTop: 10 }}>
              <button className="grow" onClick={() => setChosen([])}>Clear</button>
              <button
                className="grow primary"
                disabled={!complete || !mine}
                onClick={() => {
                  client.act({ type: 'trade', cards: selectedCards });
                  setChosen([]);
                }}
              >
                Trade
              </button>
            </div>
          </div>
        )}
      </div>

      {mine && <Advance label="No trade — continue" client={client} />}
    </>
  );
}

function NameCard({ onSave, onCancel }: { onSave: (card: Card) => void; onCancel: () => void }): ReactNode {
  const [territory, setTerritory] = useState<string>('');
  const [symbol, setSymbol] = useState<CardSymbol>('infantry');

  return (
    <div className="card" style={{ marginTop: 12, marginBottom: 0, background: 'var(--panel-2)' }}>
      <h3>What is on the card?</h3>
      <div className="stack">
        <select value={symbol} onChange={(e) => setSymbol(e.target.value as CardSymbol)}>
          {SYMBOLS.map((s) => (
            <option key={s} value={s}>{SYMBOL_GLYPH[s]} {s}</option>
          ))}
        </select>
        {symbol !== 'wild' && (
          <select value={territory} onChange={(e) => setTerritory(e.target.value)}>
            <option value="">Choose a territory…</option>
            {classic42.territories.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        )}
        <div className="row">
          <button className="grow" onClick={onCancel}>Cancel</button>
          <button
            className="grow primary"
            disabled={symbol !== 'wild' && territory === ''}
            onClick={() =>
              onSave({
                id: crypto.randomUUID(),
                territory: symbol === 'wild' ? null : territory,
                symbol,
              })
            }
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

// --- placing -------------------------------------------------------------

function PlacePanel({ state, client, mine }: { state: ClientState; client: GameClient; mine: boolean }): ReactNode {
  const { game, seatId } = state;
  const remaining = game.turn?.armiesToPlace ?? 0;

  const breakdown = seatId && game.rules
    ? computeReinforcements({ index: client.index, seat: seatId, ownerOf: ownerMap(game), rules: game.rules })
    : null;

  return (
    <>
      <div className="card">
        <h2>Place {remaining} {remaining === 1 ? 'army' : 'armies'}</h2>
        {breakdown && (
          <div className="breakdown">
            <span className="muted">{breakdown.territoryCount} territories ÷ {game.rules?.reinforcement.divisor}</span>
            <span className="mono">{breakdown.fromTerritories}</span>
            {breakdown.continents.map((c) => (
              <span className="muted" key={c.continent} style={{ display: 'contents' }}>
                <span className="muted">
                  {classic42.continents.find((x) => x.id === c.continent)?.name}
                </span>
                <span className="mono">{c.bonus}</span>
              </span>
            ))}
            {remaining !== breakdown.fromTerritories + breakdown.fromContinents && (
              <>
                <span className="muted">from cards</span>
                <span className="mono">
                  {remaining - breakdown.fromTerritories - breakdown.fromContinents}
                </span>
              </>
            )}
            <span className="total">still to place</span>
            <span className="total mono">{remaining}</span>
          </div>
        )}
        <p className="small muted" style={{ marginBottom: 0 }}>
          Tap a territory to add one. Move the real pieces as you go.
        </p>
      </div>

      <BoardView
        game={game}
        seatId={seatId}
        prefer="list"
        filter={(id) => game.territories[id]?.owner === seatId}
        onSelect={
          mine && remaining > 0
            ? (id) => client.act({ type: 'place', placements: [{ territory: id, count: 1 }] })
            : undefined
        }
      />

      {mine && <Advance label={remaining > 0 ? `${remaining} left` : 'Done placing'} client={client} />}
    </>
  );
}

// --- attacking -----------------------------------------------------------

function AttackPanel({ state, client, mine }: { state: ClientState; client: GameClient; mine: boolean }): ReactNode {
  const { game, seatId } = state;
  const battle = game.turn?.pendingBattle ?? null;
  const [from, setFrom] = useState<TerritoryId | null>(null);
  const [to, setTo] = useState<TerritoryId | null>(null);

  const owners = ownerMap(game);
  const targets = from && seatId ? new Set(attackTargets(client.index, from, owners)) : new Set<string>();

  if (battle) return <BattlePanel state={state} client={client} mine={mine} />;

  const fromArmies = from ? game.territories[from]?.armies ?? 0 : 0;
  const toArmies = to ? game.territories[to]?.armies ?? 0 : 0;
  const maxDice = Math.min(game.rules?.combat.maxAttackDice ?? 3, Math.max(0, fromArmies - 1));
  const odds = from && to && fromArmies > 1 ? computeBattleOdds(fromArmies - 1, toArmies) : null;

  return (
    <>
      <div className="card">
        <h2>Attack</h2>
        <p className="small muted" style={{ marginTop: 0 }}>
          {from === null
            ? 'Tap the territory you are attacking from.'
            : to === null
              ? `Attacking from ${nameOf(from)}. Tap where you are attacking.`
              : `${nameOf(from)} → ${nameOf(to)}`}
        </p>

        {odds && (
          <div className="odds">
            <span>
              <div className="figure">{Math.round(odds.attackerWinProbability * 100)}%</div>
              <div className="label">you take it</div>
            </span>
            <span>
              <div className="figure">{odds.expectedAttackerLosses.toFixed(1)}</div>
              <div className="label">armies you lose</div>
            </span>
            <span>
              <div className="figure">{fromArmies - 1} v {toArmies}</div>
              <div className="label">attacking · defending</div>
            </span>
          </div>
        )}

        {from && to && (
          <div className="row" style={{ marginTop: 12 }}>
            {[1, 2, 3].filter((n) => n <= maxDice).map((n) => (
              <button
                key={n}
                className="grow primary"
                disabled={!mine}
                onClick={() => {
                  client.act({ type: 'declareAttack', from, to, attackerDice: n });
                  setFrom(null);
                  setTo(null);
                }}
              >
                Roll {n}
              </button>
            ))}
          </div>
        )}
        {(from || to) && (
          <button className="ghost small" style={{ marginTop: 8 }} onClick={() => { setFrom(null); setTo(null); }}>
            Start over
          </button>
        )}
      </div>

      <BoardView
        game={game}
        seatId={seatId}
        prefer="map"
        selected={from}
        targets={targets as ReadonlySet<string>}
        onSelect={
          mine
            ? (id) => {
                if (from === null) {
                  if (game.territories[id]?.owner === seatId) setFrom(id);
                } else if (id === from) {
                  setFrom(null);
                  setTo(null);
                } else {
                  setTo(id);
                }
              }
            : undefined
        }
      />

      {mine && <Advance label="Done attacking" client={client} />}
    </>
  );
}

function BattlePanel({ state, client, mine }: { state: ClientState; client: GameClient; mine: boolean }): ReactNode {
  const { game } = state;
  const battle = game.turn!.pendingBattle!;
  const defenderDice = battle.defenderDice ?? 1;
  const [attacker, setAttacker] = useState<number[]>(() => Array(battle.attackerDice).fill(0));
  const [defender, setDefender] = useState<number[]>(() => Array(defenderDice).fill(0));

  useEffect(() => {
    setAttacker(Array(battle.attackerDice).fill(0));
    setDefender(Array(defenderDice).fill(0));
  }, [battle.attackerDice, defenderDice, battle.from, battle.to]);

  const ready = attacker.every((d) => d > 0) && defender.every((d) => d > 0);

  return (
    <div className="card">
      <h2>{nameOf(battle.from)} → {nameOf(battle.to)}</h2>
      <p className="small muted" style={{ marginTop: 0 }}>
        Roll the real dice, then tap in what they show.
      </p>

      <DiceRow label="Attacker" values={attacker} onChange={setAttacker} disabled={!mine} />
      <DiceRow label="Defender" values={defender} onChange={setDefender} disabled={!mine} />

      <button
        className="primary"
        style={{ width: '100%', marginTop: 12 }}
        disabled={!ready || !mine}
        onClick={() => client.act({ type: 'submitDice', attacker, defender })}
      >
        Apply the result
      </button>
      <button
        className="ghost"
        style={{ width: '100%', marginTop: 8 }}
        disabled={!mine}
        onClick={() => client.act({ type: 'endAttack' })}
      >
        Stop attacking {nameOf(battle.to)}
      </button>
    </div>
  );
}

function DiceRow({
  label,
  values,
  onChange,
  disabled,
}: {
  label: string;
  values: number[];
  onChange: (next: number[]) => void;
  disabled: boolean;
}): ReactNode {
  return (
    <div style={{ marginTop: 12 }}>
      <h3>{label}</h3>
      <div className="stack">
        {values.map((value, i) => (
          <div className="dicerow" key={i}>
            {[1, 2, 3, 4, 5, 6].map((face) => (
              <button
                key={face}
                className={`die ${value === face ? 'primary' : ''}`}
                disabled={disabled}
                onClick={() => {
                  const next = [...values];
                  next[i] = face;
                  onChange(next);
                }}
              >
                {face}
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function OccupyPanel({ state, client, mine }: { state: ClientState; client: GameClient; mine: boolean }): ReactNode {
  const occupation = state.game.turn!.pendingOccupation!;
  const [armies, setArmies] = useState(occupation.minimumArmies);

  return (
    <div className="card">
      <h2>You took {nameOf(occupation.to)}</h2>
      <p className="small muted" style={{ marginTop: 0 }}>
        Move at least {occupation.minimumArmies} in — as many as the dice you rolled.
        <strong> Move the pieces on the board to match.</strong>
      </p>
      <div className="row between" style={{ margin: '14px 0' }}>
        <span>Armies moving in</span>
        <Stepper
          value={armies}
          min={occupation.minimumArmies}
          max={occupation.maximumArmies}
          onChange={setArmies}
          label="armies"
        />
      </div>
      <div className="row">
        <button className="small" onClick={() => setArmies(occupation.maximumArmies)}>Move all</button>
        <button className="small" onClick={() => setArmies(occupation.minimumArmies)}>Minimum</button>
      </div>
      <button
        className="primary"
        style={{ width: '100%', marginTop: 12 }}
        disabled={!mine}
        onClick={() => client.act({ type: 'occupy', armies })}
      >
        Move {armies} in
      </button>
    </div>
  );
}

// --- fortify, card, audit ------------------------------------------------

function FortifyPanel({ state, client, mine }: { state: ClientState; client: GameClient; mine: boolean }): ReactNode {
  const { game, seatId } = state;
  const [from, setFrom] = useState<TerritoryId | null>(null);
  const [armies, setArmies] = useState(1);

  const owners = ownerMap(game);
  const reachable = from && seatId && game.rules
    ? new Set(fortifyTargets(client.index, from, seatId, owners, game.rules))
    : new Set<string>();
  const most = from ? Math.max(0, (game.territories[from]?.armies ?? 1) - 1) : 0;
  const allowed = game.rules?.fortify.movesPerTurn ?? 1;
  const used = game.turn?.fortifiesUsed ?? 0;
  const spent = used >= allowed;

  return (
    <>
      <div className="card">
        <h2>Fortify</h2>
        <p className="small muted" style={{ marginTop: 0 }}>
          {spent
            ? 'You have made your move this turn.'
            : from === null
              ? `Tap where the armies are coming from. ${allowed === 1 ? 'One move per turn.' : `${allowed - used} moves left.`}`
              : `Moving from ${nameOf(from)} — tap the destination.`}
        </p>
        {from && !spent && (
          <div className="row between" style={{ marginTop: 10 }}>
            <span>Armies to move</span>
            <Stepper value={armies} min={1} max={Math.max(1, most)} onChange={setArmies} label="armies" />
          </div>
        )}
      </div>

      <BoardView
        game={game}
        seatId={seatId}
        prefer="map"
        selected={from}
        targets={reachable as ReadonlySet<string>}
        onSelect={
          mine && !spent
            ? (id) => {
                if (from === null) {
                  if (game.territories[id]?.owner === seatId) { setFrom(id); setArmies(1); }
                } else if (id === from) {
                  setFrom(null);
                } else if (reachable.has(id)) {
                  client.act({ type: 'fortify', from, to: id, armies });
                  setFrom(null);
                }
              }
            : undefined
        }
      />

      {mine && <Advance label={spent ? 'Done' : 'Skip fortifying'} client={client} />}
    </>
  );
}

function CardPanel({ state, client, mine }: { state: ClientState; client: GameClient; mine: boolean }): ReactNode {
  const { game, seatId } = state;
  const hand = seatId ? game.hands[seatId] : undefined;
  const slots = hand?.visible ? hand.slots : [];
  const newest = slots.length - 1;
  const [naming, setNaming] = useState(false);

  return (
    <>
      <div className="card">
        <h2>You took ground — take a card</h2>
        <p className="small muted" style={{ marginTop: 0 }}>
          One card per turn, however many territories you took. Name it now or
          leave it until you trade.
        </p>
        {newest >= 0 && slots[newest]!.kind === 'unknown' && !naming && (
          <button style={{ width: '100%' }} onClick={() => setNaming(true)} disabled={!mine}>
            Name the card
          </button>
        )}
        {newest >= 0 && slots[newest]!.kind === 'known' && (
          <p className="small">Recorded.</p>
        )}
        {naming && (
          <NameCard
            onCancel={() => setNaming(false)}
            onSave={(card) => {
              client.act({ type: 'identifyCard', slot: newest, card });
              setNaming(false);
            }}
          />
        )}
      </div>
      {mine && <Advance label="Continue" client={client} />}
    </>
  );
}

function AuditPanel({ state, client, mine }: { state: ClientState; client: GameClient; mine: boolean }): ReactNode {
  const { game, seatId } = state;
  const owned = Object.entries(game.territories)
    .filter(([, t]) => t.owner === seatId)
    .map(([id]) => id);

  return (
    <>
      <div className="card">
        <h2>Check the board</h2>
        <p className="small muted" style={{ marginTop: 0 }}>
          Compare these {owned.length} counts against the table. Fix anything that
          drifted — the odds are only as good as these numbers.
        </p>
      </div>

      <TerritoryList
        game={game}
        seatId={seatId}
        filter={(id) => game.territories[id]?.owner === seatId}
        trailing={(id) => {
          const territory = game.territories[id]!;
          if (!mine) return null;
          return (
            <span className="row" style={{ gap: 4 }}>
              <button
                className="small"
                aria-label={`fewer armies in ${id}`}
                onClick={() => client.act({ type: 'adjustAudit', territory: id, armies: Math.max(0, territory.armies - 1) })}
              >−</button>
              <button
                className="small"
                aria-label={`more armies in ${id}`}
                onClick={() => client.act({ type: 'adjustAudit', territory: id, armies: territory.armies + 1 })}
              >+</button>
            </span>
          );
        }}
      />

      {mine && <Advance label="All correct — end turn" client={client} />}
    </>
  );
}
